import { useState } from "react";

import { useCartContext } from "../../../contexts/CartContext";
import { CartItem } from "./models/CartItem";
import { useOrderActionHandler } from "./hooks/useOrderActionsHandler";
import { OrderCreateViewModel } from "./OrderCreate.Component";
import { useNavigate } from "react-router";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { t } from "i18next";
import {
  ExtraLogo,
  HeatingOption,
} from "../order-confirmation/dialogs/add-heating-fee-order-dialog";
import { useErrorToast, useSuccessToast } from "../../components/Toast/Toast";
import { validateOrderMinimumQuantities } from "../../../utils/functions/QuantityUtil";

export function useOrderCreateViewModel(): OrderCreateViewModel {
  const navigate = useNavigate();
  const { profile } = useProfileContext();

  const [selectedOrderLineId, setSelectedOrderLineId] = useState<
    string | undefined
  >(undefined);
  const { items, setHeatingOption, setExtraLogos } = useCartContext();
  const { handleRemoveOrderLine, handleCreateOrder, isLoading } =
    useOrderActionHandler();

  if (selectedOrderLineId == undefined && items.length > 0) {
    setSelectedOrderLineId(items[0].id);
  }

  function removeOrderLineHandler(orderLine: CartItem) {
    handleRemoveOrderLine(orderLine.id);

    if (selectedOrderLineId == orderLine.id) {
      setSelectedOrderLineId(undefined);
    }
  }

  function selectOrderLineHandler(orderLine: CartItem) {
    setSelectedOrderLineId(orderLine.id);
  }

  interface ContextError {
    message: string;
    source: string;
    statusCode: number;
  }

  async function handleContinue(
    heatingOption: HeatingOption,
    extraLogos: ExtraLogo[]
  ): Promise<void> {
    // Validate minimum quantities before proceeding
    const validationResult = validateOrderMinimumQuantities(items);

    if (!validationResult.isValid) {
      // Show error message for the first invalid logo
      const firstInvalidLogo = validationResult.invalidLogos[0];
      const errorMessage = `Logo ${firstInvalidLogo.logoSku} doesn't fulfill minimum required quantity to order. Current: ${firstInvalidLogo.totalQuantity}, Required: ${firstInvalidLogo.minimumRequired}`;

      useErrorToast(errorMessage);
      return; // Don't proceed with order creation
    }

    const orgId = profile?.userOrganizationInformations?.organizationId;
    if (!orgId) {
      useErrorToast("Organization not available");
      return;
    }

    // Update context with selected heating options so cart summary reflects it
    setHeatingOption(heatingOption);
    setExtraLogos(extraLogos);

    return handleCreateOrder(orgId, items, heatingOption, extraLogos)
      .then(() => {
        navigate(`/order/confirm`);
      })
      .catch((error) => {
        console.log(error);
        const contextError = error as ContextError;
        const compoundErrorMessage =
          t("toasts.orders_create_failed") +
          (contextError ? ": " + contextError.message : "");
        useErrorToast(compoundErrorMessage);
      });
  }

  const isValid = items?.length > 0;

  return {
    handleRemoveOrderLine,
    orderlines: items,
    selectedOrderLineId,
    selectOrderLineHandler,
    setSelectedOrderLineId,
    removeOrderLineHandler,
    handleContinue,
    isLoading,
    isValid,
  };
}
